import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
} from '@angular/core';
import * as bootstrap from 'bootstrap';

@Directive({
  selector: '[libCollapse]',
  exportAs: 'lib-collapse',
  standalone: false,
})
export class CollapseDirective implements OnDestroy {
  private openedHeight = 0;
  private queueHide = false;
  private queueShow = false;

  readonly visible = output<boolean>();

  readonly libCollapse = input.required<boolean>();

  _isCollapsed = false;

  private collapseObject!: bootstrap.Collapse;

  private readonly el = inject(ElementRef);
  private readonly zone = inject(NgZone);

  constructor() {
    effect(() => {
      const value = this.libCollapse();
      this._isCollapsed = value;
      if (!this.collapseObject) {
        this.initCollapse(value);
      }

      if (value) {
        if (this.el.nativeElement.classList.contains('collapsing')) {
          this.queueHide = true;
        } else {
          this.collapseObject.hide();
        }
      } else {
        if (this.el.nativeElement.classList.contains('collapsing')) {
          this.queueShow = true;
        } else {
          this.zone.runOutsideAngular(() => {
            setTimeout(() => {
              this.collapseObject.show();
            });
          });
        }
      }
    });
  }

  ngOnDestroy(): void {
    const nativeElement = this.el.nativeElement as HTMLElement;
    nativeElement.removeEventListener('show.bs.collapse', this.showEvent);
    nativeElement.removeEventListener('shown.bs.collapse', this.shownEvent);
    nativeElement.removeEventListener('hide.bs.collapse', this.hideEvent);
    nativeElement.removeEventListener('hidden.bs.collapse', this.hiddenEvent);
  }

  initCollapse(collapse: boolean): void {
    this.collapseObject = new bootstrap.Collapse(this.el.nativeElement, {
      toggle: false,
    });

    const nativeElement = this.el.nativeElement as HTMLElement;
    nativeElement.classList.add('collapse');
    if (!collapse) {
      nativeElement.classList.add('show');
    }

    nativeElement.addEventListener('show.bs.collapse', this.showEvent);
    nativeElement.addEventListener('shown.bs.collapse', this.shownEvent);
    nativeElement.addEventListener('hide.bs.collapse', this.hideEvent);
    nativeElement.addEventListener('hidden.bs.collapse', this.hiddenEvent);
  }

  private showEvent = () => {
    if (this._isCollapsed) {
      return;
    }
    this.visible.emit(true);
  };

  private shownEvent = () => {
    if (this.queueHide) {
      this.queueHide = false;
      this.collapseObject.hide();
    }
    if (this._isCollapsed) {
      return;
    }
    this.openedHeight = (this.el.nativeElement as HTMLElement).scrollHeight;
  };

  private hideEvent = () => {
    if (!this._isCollapsed) {
      return;
    }
    (this.el.nativeElement as HTMLElement).style.height =
      `${this.openedHeight}px`;
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        (this.el.nativeElement as HTMLElement).style.height = '';
      });
    });
  };

  private hiddenEvent = () => {
    if (this.queueShow) {
      this.queueShow = false;
      this.collapseObject.show();
    }
    if (!this._isCollapsed) {
      return;
    }
    this.visible.emit(false);
  };
}
